import { message } from 'antd';
import '../styles/ToastMessage.css';

/**
 * Centralized toast messaging system
 * Use this component to display consistent toast messages across the application
 */
const ToastMessage = {
  /**
   * Show a success toast message
   * @param {string|object} content - Message content or config object
   * @param {number} duration - Duration in seconds (default: 3)
   * @param {object} style - Optional additional inline styles
   */
  success: (content, duration = 3, style = {}) => {
    if (typeof content === 'string') {
      message.success({
        content,
        duration,
        className: 'custom-toast custom-toast-success',
        style,
      });
    } else {
      message.success({
        ...content,
        className: `custom-toast custom-toast-success ${content.className || ''}`,
        style: { ...style, ...content.style },
      });
    }
  },

  /**
   * Show an error toast message
   * @param {string|object} content - Message content or config object
   * @param {number} duration - Duration in seconds (default: 5)
   * @param {object} style - Optional additional inline styles
   */
  error: (content, duration = 5, style = {}) => {
    if (typeof content === 'string') {
      message.error({
        content,
        duration,
        className: 'custom-toast custom-toast-error',
        style,
      });
    } else {
      message.error({
        ...content,
        className: `custom-toast custom-toast-error ${content.className || ''}`,
        style: { ...style, ...content.style },
      });
    }
  },

  /**
   * Show a warning toast message
   * @param {string|object} content - Message content or config object
   * @param {number} duration - Duration in seconds (default: 4)
   * @param {object} style - Optional additional inline styles
   */
  warning: (content, duration = 4, style = {}) => {
    if (typeof content === 'string') {
      message.warning({
        content,
        duration,
        className: 'custom-toast custom-toast-warning',
        style,
      });
    } else {
      message.warning({
        ...content,
        className: `custom-toast custom-toast-warning ${content.className || ''}`,
        style: { ...style, ...content.style },
      });
    }
  },

  /**
   * Show an info toast message
   * @param {string|object} content - Message content or config object
   * @param {number} duration - Duration in seconds (default: 3)
   * @param {object} style - Optional additional inline styles
   */
  info: (content, duration = 3, style = {}) => {
    if (typeof content === 'string') {
      message.info({
        content,
        duration,
        className: 'custom-toast custom-toast-info',
        style,
      });
    } else {
      message.info({
        ...content,
        className: `custom-toast custom-toast-info ${content.className || ''}`,
        style: { ...style, ...content.style },
      });
    }
  },

  /**
   * Show a loading toast message
   * @param {string|object} content - Message content or config object
   * @param {number} duration - Duration in seconds (default: 0 for indefinite)
   * @param {object} style - Optional additional inline styles
   * @returns {MessageType} The message instance for manual control
   */
  loading: (content, duration = 0, style = {}) => {
    if (typeof content === 'string') {
      return message.loading({
        content,
        duration,
        className: 'custom-toast custom-toast-loading',
        style,
      });
    } else {
      return message.loading({
        ...content,
        className: `custom-toast custom-toast-loading ${content.className || ''}`,
        style: { ...style, ...content.style },
      });
    }
  },

  /**
   * Configure global message options
   * @param {object} options - Global configuration options
   */
  config: (options) => {
    message.config(options);
  }
};

export default ToastMessage; 